import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { MST1060_R10Dto, defaultMST1060_R10Dto } from "./Dto_MST1060_R10";
import { useMST1060_R10Store } from "./Store_MST1060_R10";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from "src/utils/grid/gridStyleUtils";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { barcodeCellRenderer } from "./BarcodeRenderer";
import useUiuxStore from "src/store/useUiuxStore";
import { 
	CreateCellClickHandler, 
	CreateCellDoubleClickHandler, 
	CustomCellClickAction, 
	CustomCellDoubleClickAction, 
	RemoveRowHandler, 
	useRowSelectionHandler,
	createCellCssClassRules,
	GridCellConfig,
	createGridDropDownCommitAfter
} from "src/utils/grid/GridHandlerContent";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";

import { createModuleConfig } from "src/utils/detail-table/detailTableUtils";
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";
import { headerNames_MST1060_R10 } from "./Util_MST1060_R01";
import { useCodeSearch } from "src/hooks/useCodeSearch";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";

const COLUMN_STATE_KEY = 'MST1060_R10-barcode-grid-column-state';

const GRID_CELL_CONFIG: GridCellConfig = {
	disabledCells: ['itemNm','barcodeImage', 'itemSpec', 'itemUnit'],
	insertedDisabledCells: ['itemNm','barcodeImage', 'itemSpec', 'itemUnit'],
	commonDisabledCells: ['barcode'],
	
	pointerCells: ['itemCd', 'packQty'],
	insertedPointerCells: ['barcode', 'itemCd', 'itemNm', 'itemSpec', 'itemUnit', 'packQty'],
	commonPointerCells: [''],
	
	alignLeftCells: ['barcode']
};

interface Props {
	gridRef: React.RefObject<AgGridReact<MST1060_R10Dto> | null>;
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	defaultMST1060_R10Row: MST1060_R10Dto;
	handleInsertClick: (e: React.MouseEvent, rowIndex?: number) => void;
}

export const MST1060_R10Table = (props: Props) => {
	const { gridRef, clickable, setClickable, defaultMST1060_R10Row, handleInsertClick } = props;

	const { 
		barcodeList, 
		selectedBarcode,
        setBarcodeList,
		selectedBarcodeList,
		deletedBarcodeList,
		removeBarcodeList,
		updateBarcodeList,
		originalBarcodeList,
		addDeletedBarcodeList,
		removeSelectedBarcodeList,
		addSelectedBarcodeList,
		initSelectedBarcodeList,
		columnState,
		setColumnState,
		scrollTop,
		setScrollTop,
	} = useMST1060_R10Store();
	
	const { unitList } = useCommonCodeStore();
	const { setCodeSearchModalProps } = useUiuxStore();

	// 품목 자동완성을 위한 코드 검색 훅
const { searchExactCode } = useCodeSearch({});

	// MST1060 모듈 설정 생성
	const MST1060Config = createModuleConfig.MST1060();

	// 검색 함수를 설정에 주입
	const fieldConfigWithSearch = {
		...MST1060Config.fieldConfig,
		codeSearchFields: {
			...MST1060Config.fieldConfig.codeSearchFields,
			itemCd: {
				...MST1060Config.fieldConfig.codeSearchFields!.itemCd,
				searchFunction: (code: string, onSuccess?: (item: any) => void) => 
					searchExactCode('item', code, onSuccess)
			}
		}
	};

	const { 
		getRowId,
        createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef: gridRef as any,
		key: 'valueIndex',
		gridData: barcodeList,
		defaultGridData: defaultMST1060_R10Row,
		selectedRow: selectedBarcode || undefined,
		originalGridData: originalBarcodeList,
		updateRow: (index: number, data: any) => updateBarcodeList(index, data),
	});

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = createCellCssClassRules(GRID_CELL_CONFIG);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<MST1060_R10Dto>({
			idKey: 'valueIndex',
			selectedList: selectedBarcodeList,
			deletedList: deletedBarcodeList,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [createCellClassRules, selectedBarcodeList, deletedBarcodeList, disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns]);

	// 상세 테이블 공통 핸들러 (자동완성 포함)
	const detailTableHandlers = useCommonDetailTableHandlers<MST1060_R10Dto>({
		detailList: barcodeList,
		updateDetailList: (valueIndex: number, data: MST1060_R10Dto) => {
			// valueIndex를 배열 인덱스로 변환
			const arrayIndex = barcodeList.findIndex(b => b.valueIndex === valueIndex);
			if (arrayIndex !== -1) {
				updateBarcodeList(arrayIndex, data);
			}
		},
		originalDetailList: originalBarcodeList,
		setCodeSearchModalProps,
		gridRef: gridRef as any,
		fieldConfig: fieldConfigWithSearch,
		codeSearchConfig: MST1060Config.codeSearchConfig,
		calculateAmount: (data) => data, // 바코드는 금액 계산 불필요
	});

	// 편집 가능한 컬럼 정의
	const commonInputColumns = ['barcode', 'itemCd', 'packQty'];
	const defaultEditableColumns = ['barcode', 'itemCd', 'packQty'];

	const handleGridDropDownCommitAfter = useMemo(() => 
		createGridDropDownCommitAfter<MST1060_R10Dto>(defaultEditableColumns),
	[defaultEditableColumns],);

	const baseColumnDefs: ColDef<MST1060_R10Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			(params: MST1060_R10Dto) => renderIndexCell(
				params,
				(innerParams: MST1060_R10Dto) => getRowStyleFlags(
					innerParams,
					{
						deletedList: deletedBarcodeList,
						selectedList: selectedBarcodeList,
						dataList: barcodeList
					}
				),
				(rowIndex: number, e: React.MouseEvent) => {
					if (e.ctrlKey) {
						for (let i = 0; i < 10; i++) { handleInsertClick(e, rowIndex); }
					} else { 
						handleInsertClick(e, rowIndex);
					}
				},
				(rowData: MST1060_R10Dto) => RemoveRowHandler(
					gridRef,
					barcodeList,
					rowData,
					setClickable,
					(valueIndex: number) => {
						// valueIndex를 배열 인덱스로 변환
						const arrayIndex = barcodeList.findIndex(b => b.valueIndex === valueIndex);
						if (arrayIndex !== -1) {
							removeBarcodeList(arrayIndex);
						}
					},
					(valueIndexes: string[]) => addDeletedBarcodeList(valueIndexes)
				)
			)
		),
		{
			headerName: '바코드', colId: 'barcode', field: 'barcode', width: 150,
			editable: params => Boolean(params.data?.inserted),
			pasteable: true,
			valueSetter: params => {
				if (!params.data?.inserted || params.data.barcode === params.newValue) {
					return false;
				}
				params.data.barcode = params.newValue;
				return true;
			},
		},
		{
			headerName: '실제코드', colId: 'barcodeImage', field: 'barcode', width: 250,
			cellRenderer: barcodeCellRenderer,
			editable: false,
			cellStyle: {
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				padding: '4px'
			},
		},
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 120,
			editable: true,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
		},
		{
			headerName: '품목명', colId: 'itemNm', field: 'itemNm', width: 200,
			editable: false,
		},
		{
			headerName: '품목규격', colId: 'itemSpec', field: 'itemSpec', width: 180,
			editable: false,
		},
		{
			headerName: '포장수량', colId: 'packQty', field: 'packQty', width: 100,
			editable: true,
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replaceAll(',', '').trim()) || 0,
		},
		{
			headerName: '포장단위', colId: 'itemUnit', field: 'itemUnit', width: 100,
			...CommonCodeColumn.options(unitList, false, CommonCodeColumn.change(updateBarcodeList), handleGridDropDownCommitAfter),
		},
	], [unitList, updateBarcodeList, handleGridDropDownCommitAfter, renderIndexCell, deletedBarcodeList, selectedBarcodeList, barcodeList, handleInsertClick, gridRef, setClickable, removeBarcodeList, addDeletedBarcodeList]);

	const columnDefs: ColDef<MST1060_R10Dto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<MST1060_R10Dto>({
			baseColumnDefs,
			columnState,
			headerNames: headerNames_MST1060_R10,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, columnState]);

	const handleRowClick = useRowSelectionHandler({
		dataList: barcodeList,
		idKey: 'valueIndex',
		selectedList: selectedBarcodeList,
		deletedList: deletedBarcodeList,
		addSelected: addSelectedBarcodeList,
		removeSelected: removeSelectedBarcodeList,
		clickable, 
		setClickable
	});

	// 범용 이벤트 훅 사용
	const cellEventHandlers = useDetailTableEvents({
		detailList: barcodeList as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});

	// 셀 클릭 액션
	const cellClickActions: CustomCellClickAction<MST1060_R10Dto>[] = useMemo(() => [
		{
			validate: (field, data) =>
				commonInputColumns.includes(field) && (field !== 'barcode' || Boolean(data.inserted)),
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], [commonInputColumns]);
	const handleCellClick = CreateCellClickHandler(barcodeList, 'valueIndex', cellClickActions);

	// 더블클릭 액션
	const cellDoubleClickActions: CustomCellDoubleClickAction<MST1060_R10Dto>[] = useMemo(() => [
		{
			validate: (field) => (field === 'itemCd' || field === 'itemNm' || field === 'itemSpec' || field === 'itemUnit'),
			action: (_e, foundData) => {
				detailTableHandlers.settingCodeSearchModalProps('품목', foundData.itemCd!, false, 'itemCd', foundData, foundData.valueIndex!);
			}
		},
	], [detailTableHandlers]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(barcodeList, 'valueIndex', cellDoubleClickActions);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		barcode: barcodeList.length.toLocaleString() + "건",
		itemCd: '',
		itemNm: '',
		itemSpec: '',
		packQty: 0,
		itemUnit: '',
		remark: '',
		editable: false,
	}]), [barcodeList]);

	return (
		<GridContextWrapper<MST1060_R10Dto>
			gridRef={gridRef}
			paddedGridData={paddedGridData as any}
			enableCellCalculation
			setPaddedGridData={setBarcodeList}
			initSelected={initSelectedBarcodeList}
			defaultRowData={defaultMST1060_R10Dto}
			baseColumnDefs={columnDefs}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<MST1060_R10Dto>
				ref={gridRef}
				rowData={paddedGridData as any}
				getRowId={getRowId as any}
				columnDefs={columnDefs}
				defaultColDef={cellCssDefaultColDef}
				
				rowClassRules={createRowClassRules<MST1060_R10Dto>({
					idKey: 'valueIndex',
					selectedList: selectedBarcodeList,
					deletedList: deletedBarcodeList
				})}
				onRowClicked={handleRowClick}
				onCellClicked={handleCellClick}
				onCellDoubleClicked={handleCellDoubleClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={detailTableHandlers.handleCellChange}
				pinnedBottomRowData={pinnedBottomRow}

				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};

